"use client"

import { useState } from "react"

import { Link, useNavigate } from "react-router-dom"
import Card from "../components/Card"
import Button from "../components/Button"
import Alert from "../components/Alert"
import { LogIn, ArrowLeft } from "react-feather"
import Footer from "../components/Footer"

const Login = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alert, setAlert] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setAlert(null)

      const response = await fetch(`${API_BASE_URL}/api/Auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          remember: formData.remember,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || "Login failed")
      }

      localStorage.setItem("userToken", data.token)

      setAlert({
        type: "success",
        message: "Login successful! Redirecting...",
      })

      setTimeout(() => {
        navigate("/Home")
      }, 1500)
    } catch (error) {
      console.error("Login error:", error)
      setAlert({
        type: "error",
        message: error.message || "Login failed. Please check your credentials.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 pb-4">
      <div className="sm:mx-auto sm:w-full sm:max-w-4xl">
        <Card className="overflow-hidden shadow-xl">
          <div className="flex flex-col md:flex-row-reverse"> {/* Changed to flex-row-reverse */}
            {/* Right Side - Branding (now on the right) */}
            <div className="bg-gradient-to-br from-[#20C5C3] to-[#178a89] p-8 md:w-1/3 flex flex-col justify-center items-center text-center text-white">
              <div className="mb-6">
                <h1 className="text-3xl font-bold mb-4">Welcome Back</h1>
                <p className="opacity-90">Sign in to access your administrator dashboard</p>
              </div>
              
              <div className="mt-8">
                <p className="mb-4">Don't have an account?</p>
                <Link 
                  to="/signup" 
                  className="inline-flex items-center px-4 py-2 border-2 border-white rounded-lg font-medium hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  Sign Up
                </Link>
              </div>
              
              <Link 
                to="/" 
                className="mt-8 text-sm flex items-center hover:underline"
              >
                <ArrowLeft className="mr-1" size={14} />
                Back to home
              </Link>
            </div>

            {/* Left Side - Form */}
            <div className="p-8 md:p-10 md:w-2/3 bg-white">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800">Admin Login</h2>
                <p className="text-gray-600 mt-2">Enter your credentials to continue</p>
              </div>

              {alert && (
                <Alert 
                  type={alert.type} 
                  dismissible 
                  onDismiss={() => setAlert(null)} 
                  className="mb-6"
                >
                  {alert.message}
                </Alert>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20C5C3] focus:border-[#20C5C3] outline-none transition"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20C5C3] focus:border-[#20C5C3] outline-none transition"
                      placeholder="Enter your password"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember"
                        name="remember"
                        type="checkbox"
                        checked={formData.remember}
                        onChange={handleChange}
                        className="h-4 w-4 text-[#20C5C3] focus:ring-[#20C5C3] border-gray-300 rounded"
                      />
                      <label htmlFor="remember" className="ml-2 block text-sm text-gray-900">
                        Remember me
                      </label>
                    </div>

                    <div className="text-sm">
                      <a href="#" className="font-medium text-[#20C5C3] hover:text-[#178a89]">
                        Forgot password?
                      </a>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <Button 
                    type="submit" 
                    className="w-full flex justify-center items-center py-3 px-4 bg-[#20C5C3] hover:bg-[#178a89] transition-colors"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                        Signing In...
                      </>
                    ) : (
                      <>
                        <LogIn className="w-5 h-5 mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <div className="mt-6 text-center text-sm text-gray-500">
                <p>By signing in, you agree to our <a href="#" className="text-[#20C5C3] hover:underline">Terms of Service</a></p>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  )
}

export default Login